import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Share, PlusSquare, Info, ChevronDown } from 'lucide-react'

const PWAInstallModal = ({ pwaInstalled }) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isIOS, setIsIOS] = useState(false)

  const [showInstructions, setShowInstructions] = useState(false)

  const markAsInstalled = async () => {
    localStorage.setItem('pwa-installed', 'true')
    try {
      await fetch('/api/v1/users/pwa_installed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })
    } catch (e) {
      console.error('Failed to mark PWA as installed:', e)
    }
  }

  useEffect(() => {
    // Detect platform and standalone status
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    setIsIOS(isIOSDevice)

    // Check localStorage as well for immediate feedback or secondary assurance
    const localInstalled = localStorage.getItem('pwa-installed') === 'true'

    // If already marked as installed in DB or localStorage, we don't need the modal
    if (pwaInstalled || localInstalled) {
      if (localInstalled && !pwaInstalled && isStandalone) {
        // Just in case it's in localStorage but not DB yet (e.g. previous failed fetch)
        markAsInstalled()
      }
      return
    }

    // If opened in standalone mode but not yet marked in DB, mark it now
    if (isStandalone) {
      markAsInstalled()
      return
    }

    // Only show if on mobile
    if (isMobile) {
      // If the prompt is already captured globally, show it
      if (window.deferredPWAInstallPrompt) {
        setDeferredPrompt(window.deferredPWAInstallPrompt)
        setShowModal(true)
      } else {
        // Small delay to not annoy the user immediately
        const timer = setTimeout(() => {
          setShowModal(true)
        }, 1000)
        return () => clearTimeout(timer)
      }
    }

    const handler = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setShowModal(true)
    }

    const onInstalled = () => {
      markAsInstalled()
      setShowModal(false)
    }

    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [pwaInstalled])

  const handleInstall = async () => {
    const promptToUse = deferredPrompt || window.deferredPWAInstallPrompt

    if (isIOS || !promptToUse) {
      setShowInstructions(true)
    } else {
      try {
        promptToUse.prompt()
        const { outcome } = await promptToUse.userChoice
        if (outcome === 'accepted') {
          setDeferredPrompt(null)
          window.deferredPWAInstallPrompt = null
          // No need to setShowModal(false) here, appinstalled event will handle it
        }
      } catch (e) {
        console.error('Prompt failed:', e)
        setShowInstructions(true)
      }
    }
  }

  const handleDismiss = () => {
    setShowModal(false)
    setShowInstructions(false)
  }

  const handleDismissForever = () => {
    markAsInstalled()
    setShowModal(false)
  }

  if (pwaInstalled || !showModal) return null

  return (
    <div className="fixed inset-x-4 bottom-8 z-[100] animate-in fade-in slide-in-from-bottom duration-500 md:max-w-md md:left-auto md:right-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1c1c1e] to-[#2c2c2e] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10 backdrop-blur-xl">
        {/* Background Gradient Glows */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-orange-500/10 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-orange-600/5 blur-3xl rounded-full"></div>

        <button 
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors p-1"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-start gap-5">
          <div className="w-16 h-16 shrink-0 rounded-2xl bg-white p-1 shadow-lg overflow-hidden border border-white/20">
             <img src="/icon-v2.png" alt="Anytime Tracker Icon" className="w-full h-full object-cover rounded-xl" />
          </div>
          <div className="flex-1 pr-4 pb-2">
            <h3 className="text-base font-bold text-white mb-0.5 tracking-tight">{t('pwa.install_title')}</h3>
            <p className="text-gray-400 text-[11px] leading-relaxed mb-3">
              {t('pwa.install_description')}
            </p>
            
            <div className="mt-1">
              {!showInstructions ? (
                <div className="flex flex-col items-center gap-2.5">
                  <button
                    onClick={handleInstall}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-2.5 px-6 rounded-xl shadow-lg shadow-orange-500/25 hover:opacity-90 transition-all active:scale-[0.98] text-center text-sm"
                  >
                    {t('pwa.install_button')}
                  </button>
                  <button
                    onClick={handleDismissForever}
                    className="text-gray-500 hover:text-gray-400 text-[10px] transition-colors underline underline-offset-2 py-1"
                  >
                    {t('pwa.dismiss_button')}
                  </button>
                </div>
              ) : (
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-4 animate-in fade-in zoom-in duration-300">
                  {isIOS ? (
                    <>
                      <div className="flex items-center gap-3 text-xs text-gray-300">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 border border-blue-500/20">
                          <Share size={16} className="text-blue-400" />
                        </div>
                        <p>1. {t('pwa.ios_instruction').split('「')[0]}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-300">
                        <div className="w-8 h-8 rounded-lg bg-gray-500/20 flex items-center justify-center shrink-0 border border-gray-500/20">
                          <ChevronDown size={16} />
                        </div>
                        <p>2. {t('pwa.ios_step2')}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-300">
                        <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0 border border-orange-500/20">
                          <PlusSquare size={16} className="text-orange-400" />
                        </div>
                        <p>3. {t('pwa.ios_step3')}</p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-start gap-3 text-xs text-gray-300">
                      <div className="w-8 h-8 rounded-lg bg-gray-500/20 flex items-center justify-center shrink-0 border border-gray-500/20 mt-0.5">
                        <Info size={16} className="text-gray-400" />
                      </div>
                      <p className="leading-relaxed">{t('pwa.android_instruction')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PWAInstallModal
